// In-app notifications — the signed-in user's own copies (PRD_InAppNotifications).
// The rejection triggers (functions/src/notifications/) create and delete them;
// the client only listens and marks its own copies read or dismissed (C15).

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";

const notificationsCol = (companyId) => collection(db, "companies", companyId, "notifications");
const notificationRef = (companyId, id) => doc(db, "companies", companyId, "notifications", id);

const createdMs = (n) => (n.createdAt && typeof n.createdAt.toMillis === "function" ? n.createdAt.toMillis() : 0);

/**
 * Live list of the user's notifications, newest first. One equality filter on
 * recipientUserDocId (the filter C15's read rule requires), sorted in the browser,
 * so no composite index is needed.
 * @returns {function} unsubscribe
 */
export const subscribeToNotifications = (companyId, userDocId, onChange) =>
  onSnapshot(
    query(notificationsCol(companyId), where("recipientUserDocId", "==", userDocId)),
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      onChange(items.sort((a, b) => createdMs(b) - createdMs(a)));
    },
    () => {
      // Silently ignore errors, like the side-nav badge listeners (e.g. permissions not yet set).
    }
  );

export const markRead = (companyId, id) =>
  updateDoc(notificationRef(companyId, id), { readAt: serverTimestamp() });

export const markAllRead = (companyId, ids) => {
  const batch = writeBatch(db);
  ids.forEach((id) => batch.update(notificationRef(companyId, id), { readAt: serverTimestamp() }));
  return batch.commit();
};

// C16 — hides the recipient's own copy; the trigger still deletes it when the
// rejection clears, and a redelivered trigger leaves it hidden (create-if-absent).
export const dismiss = (companyId, id) =>
  updateDoc(notificationRef(companyId, id), { dismissedAt: serverTimestamp() });
